import { Ref, computed } from 'vue'
import { CardState } from '../../types'
import {
  HeatmapResponse,
  HeatmapType,
  HeatmapValue
} from '@/api-client/models/utilization/Contracts'
import { DataFetcher } from '@/composables/useDataFetcher'
import { AnalyticFilters, FeatureType } from './useFilters'

type HeatmapMap = Record<
  HeatmapType,
  { show: boolean; accessKey: keyof HeatmapValue; props: DataSource }
>

type DataSource = {
  data: any
  state: CardState
  hasError: Error | undefined
}

function getState<Data>(dataRef: DataFetcher<Data>) {
  if (dataRef.isLoading.value) {
    return 'LOADING'
  }
  if (dataRef.error.value) {
    return 'ERROR'
  }
  return 'ACTIVE'
}

export function useHeatmapComponentMap(
  heatmapRef: DataFetcher<HeatmapResponse>,
  filterState: Ref<AnalyticFilters<FeatureType.Utilization> | undefined>
) {
  const invertDataSource = <Response>(dataSource: DataFetcher<Response>): DataSource => ({
    data: dataSource.data.value,
    hasError: dataSource.error.value,
    state: getState(dataSource)
  })

  const heatmap = computed<DataSource>(() => invertDataSource(heatmapRef))

  const heatmapMap = computed<HeatmapMap>(() => ({
    AVERAGE_USAGE: {
      show: true,
      accessKey: 'used',
      props: heatmap.value
    },
    AVERAGE_BOOKING: {
      show: filterState.value?.onlyBookable ?? false,
      accessKey: 'booked',
      props: heatmap.value
    },
    AVERAGE_USAGE_WHEN_BOOKED: {
      show: filterState.value?.onlyBookable ?? false,
      accessKey: 'usedAndBooked',
      props: heatmap.value
    },
    AVERAGE_USAGE_WHEN_NOT_BOOKED: {
      show: filterState.value?.onlyBookable ?? false,
      accessKey: 'usedNotBooked',
      props: heatmap.value
    },
    AVERAGE_BOOKING_WHEN_NOT_USED: {
      show: filterState.value?.onlyBookable ?? false,
      accessKey: 'bookedNotUsed',
      props: heatmap.value
    }
  }))

  return heatmapMap
}
